import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Checkbox, Dropdown, Field } from '../../Core';
import * as oPath from '../../../Lib/Object/Path/Immutable';
import { Grid, GridFieldSection, ListFieldSection } from '../../Layout';
import { WorksheetEquipmentDropdown } from '../../Worksheets/Core';
import { actions } from '../../../Data/ReduxViewModels/Equipment';
import deepEquals from '../../../Lib/Object/deepEquals';
import { withInjectedProps } from '../../Core/InjectedPropsContext';
import { sort, compose, descend } from 'ramda';
import isEmptyObject from 'Lib/Object/isEmptyObject';

let SieveSetSieveGridSection = ({ equipmentSelector, namePrefix }) => {
  const dispatch = useDispatch();
  const equipmentId = useSelector((state) => state.equipment.equipmentId);
  const [showCommon, setShowCommon] = useState(true);
  const [sieveRegistrationsLocal, setSieveRegistrations] = useState([]);
  const divRef = useRef(null);

  const sieveRegistrations = useSelector(
    (state) => oPath.get(equipmentSelector(state), `${namePrefix}.SieveRegistrations`) ?? [],
    deepEquals
  );

  const availableSieveSizes = useSelector((state) => {
    let allSieveSizes = oPath.get(equipmentSelector(state), `${namePrefix}.AllSieveSizes`) ?? [];
    return showCommon
      ? allSieveSizes.filter(
          (s) => !!s.IsCommon || (sieveRegistrations && sieveRegistrations.some((r) => r.SizeId === s.Id))
        )
      : allSieveSizes;
  }, deepEquals);

  const validateAvailableSieveSizes = useCallback(() => {
    if (
      !availableSieveSizes ||
      availableSieveSizes.some((s) => !s.Size || s.Size.uom !== availableSieveSizes[0].Size.uom)
    )
      throw new Error('Cannot compare sieves sizes. Sizes are missing or are defined with inconsistent units.');
  }, [availableSieveSizes]);

  const orderRegistrationsBySieveSize = useCallback(
    (registrations) => {
      validateAvailableSieveSizes();
      return sort(
        compose(
          isEmptyObject,
          descend((r) => Number(availableSieveSizes.find((s) => s.Id === r.SizeId)?.Size.value))
        ),
        registrations
      );
    },
    [availableSieveSizes, validateAvailableSieveSizes]
  );

  useEffect(() => {
    // Update local sieve registrations if they don't match the app state registrations
    // Ignore empty local registrations when comparing and put these back,
    // as these only exist locally until data is filled (to avoid posting empty sieve registrations)
    let sieveRegistrationsAppState = orderRegistrationsBySieveSize(sieveRegistrations);
    if (
      !deepEquals(
        sieveRegistrationsAppState,
        sieveRegistrationsLocal.filter((s) => !isEmptyObject(s))
      )
    ) {
      setSieveRegistrations([...sieveRegistrationsAppState, ...sieveRegistrationsLocal.filter(isEmptyObject)]);
    }
  }, [sieveRegistrations, sieveRegistrationsLocal, availableSieveSizes, orderRegistrationsBySieveSize]);

  const getSieveOptions = (i) => {
    validateAvailableSieveSizes();

    const otherSieveRegistrations = sieveRegistrationsLocal.filter((_, j) => i !== j);
    const validSieveSizes = availableSieveSizes.filter(
      (s) => !otherSieveRegistrations.some((sr) => sr.SizeId === s.Id)
    );

    return sort(
      descend((s) => Number(s.Size.value)),
      validSieveSizes
    ).map((s) => ({
      value: s.Id,
      label: s.Caption,
    }));
  };

  const handleGridCreateCol = () => {
    // Only add new registrations locally (as we do not want to retain empty records)
    setSieveRegistrations([...sieveRegistrationsLocal, {}]);
    divRef.current.scrollIntoView({ behavior: 'smooth' });
  };

  const handleGridRemoveCol = (index) => {
    setSieveRegistrations(sieveRegistrationsLocal.filter((_, i) => i !== index));
    // Empty rows will not exist in the state (as we don't want to retain empty records)
    if (!isEmptyObject(sieveRegistrationsLocal[index])) {
      dispatch(actions.removeArrayEntry({ name: `${namePrefix}.SieveRegistrations`, index, sync: true }));
    }
  };

  const setSieveRegistrationSizeForRow = ({ index, value }) => {
    // Empty rows will not exist in the state (as we don't want to retain empty records)
    // If we are setting the first value on an empty row we need to make sure we apply the change to the first empty row
    // (to avoid creating gaps with undefined rows).
    if (isEmptyObject(sieveRegistrationsLocal[index])) {
      index = sieveRegistrationsLocal.findIndex(isEmptyObject);
    }

    setSieveRegistrations(
      orderRegistrationsBySieveSize(oPath.set(sieveRegistrationsLocal, `[${index}].SizeId`, value))
    );
    // Clear Sieve Id for row
    // This is for the case where an existing row sieve size is changed as the sieve id will no longer be valid
    dispatch(
      actions.setValue({ name: `${namePrefix}.SieveRegistrations[${index}].Sieve`, value: undefined, sync: true })
    );

    // Set Size for row
    dispatch(actions.setValue({ name: `${namePrefix}.SieveRegistrations[${index}].SizeId`, value: value, sync: true }));
  };

  const getLargestSieveSizeLessThanPreviousSelection = (i) => {
    const precedingSieveSize = availableSieveSizes.find(
      (s) =>
        s.Id ===
        sieveRegistrationsLocal
          .slice(0, i)
          .reverse()
          .find((s) => s && s.SizeId)?.SizeId
    )?.Size.value;

    return (
      precedingSieveSize &&
      getSieveOptions(i).find(
        (o) => Number(availableSieveSizes.find((s) => s.Id === o.value)?.Size.value) < Number(precedingSieveSize)
      )
    );
  };

  return (
    <>
      <ListFieldSection heading="Sieves in Sieve Set">
        <Field
          input={<Checkbox />}
          label="Only show common sieve sizes"
          value={showCommon}
          onChange={(e) => setShowCommon(e.target.checked)}
        />
      </ListFieldSection>
      <GridFieldSection>
        <Grid
          colHeaders={['Sieve Size', 'Sieve Id']}
          colWidths={200}
          data={sieveRegistrationsLocal?.map((s, i) => {
            var sieveSize = availableSieveSizes.find((a) => a.Id === s.SizeId);
            return [
              <Field
                input={<Dropdown />}
                options={getSieveOptions(i)}
                value={s.SizeId}
                label="Sieve Size"
                simpleValue
                onChange={(e) => setSieveRegistrationSizeForRow({ index: i, value: e.data.value })}
                focusedOption={s.SizeId ? null : getLargestSieveSizeLessThanPreviousSelection(i)}
                isClearable={false}
              />,
              <Field
                input={<WorksheetEquipmentDropdown />}
                disabled={!s.SizeId}
                name={`SieveRegistrations[${i}].Sieve`}
                value={s.Sieve}
                label="Sieve Id"
                equipmentType="sieve"
                worksheetId={equipmentId}
                queryParams={sieveSize ? { SieveSize: `${sieveSize.Size.value} ${sieveSize.Size.uom}` } : null}
              />,
            ];
          })}
          rows={sieveRegistrationsLocal?.map((s) => ({ key: s.SizeId }))}
          onCreateRow={handleGridCreateCol}
          onRemoveRow={handleGridRemoveCol}
        />
      </GridFieldSection>
      <div ref={divRef} />
    </>
  );
};

SieveSetSieveGridSection = withInjectedProps(SieveSetSieveGridSection);

export default SieveSetSieveGridSection;
export { SieveSetSieveGridSection };
